/**
 * ToR CV Search Page V2
 * 
 * Paste ToR requirements to find matching CVs using AI-powered semantic search
 * Features:
 * - Paste requirements text directly (no file upload needed)
 * - Per-item tier control (required/preferred/bonus)
 * - Add missing requirements per category
 * - Click chips to cycle tier
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    AlertTitle,
    CircularProgress,
    Chip,
    Stack,
    Grid,
    TextField,
    IconButton,
    MenuItem
} from '@mui/material';
import {
    Search as SearchIcon,
    ContentPaste as ContentPasteIcon,
    Refresh as RefreshIcon,
    Add as AddIcon
} from '@mui/icons-material';
import CVMatchingResults from '../components/CVMatchingResults';
import HelpIconButton from '../components/HelpIconButton';
import CVSourceToggle, { CVSource } from '../components/CVSourceToggle';
import { matchCVsFromTor, CVMatchingResult } from '../services/cvMatching';
import { 
    extractTorFromText, 
    RequirementItem, 
    RequirementTier,
    ConsultantRequirementsV2
} from '../services/torForCVs';

// Tier colors
const TIER_COLORS = {
    neutral: { bg: '#546e7a', border: '#455a64', text: 'white' },  // Blue-gray - readable, distinct from other tiers
    required: { bg: '#b71c1c', border: '#b71c1c', text: 'white' },
    preferred: { bg: '#e65100', border: '#e65100', text: 'white' },
    bonus: { bg: '#2e7d32', border: '#2e7d32', text: 'white' }
};

// Category configuration for display
interface CategoryConfig {
    key: string;
    label: string;
    helpKey?: string;
    getItems: (reqs: ConsultantRequirementsV2) => RequirementItem[];
    setItems: (reqs: ConsultantRequirementsV2, items: RequirementItem[]) => void;
}

const CATEGORIES: CategoryConfig[] = [
    // Row 1: Geographic Countries & Regions (side by side)
    {
        key: 'geographic_countries',
        label: 'Geographic Countries',
        helpKey: 'geographic',
        getItems: (r) => r.geographic_experience?.countries || [],
        setItems: (r, items) => { if (r.geographic_experience) r.geographic_experience.countries = items; }
    },
    {
        key: 'geographic_regions',
        label: 'Geographic Regions',
        helpKey: 'geographic',
        getItems: (r) => r.geographic_experience?.regions || [],
        setItems: (r, items) => { if (r.geographic_experience) r.geographic_experience.regions = items; }
    },
    // Row 2: Methods & Technical Domain
    {
        key: 'methods_methodologies',
        label: 'Methods & Methodologies',
        helpKey: 'methods',
        getItems: (r) => r.methods_methodologies?.items || [],
        setItems: (r, items) => { if (r.methods_methodologies) r.methods_methodologies.items = items; }
    },
    {
        key: 'technical_domain_expertise',
        label: 'Technical & Domain Expertise',
        helpKey: 'technicalDomain',
        getItems: (r) => r.technical_domain_expertise?.items || [],
        setItems: (r, items) => { if (r.technical_domain_expertise) r.technical_domain_expertise.items = items; }
    },
    // Row 3: Technical Tools & Frameworks
    {
        key: 'technical_tools_software',
        label: 'Technical Tools & Software',
        helpKey: 'technicalTools',
        getItems: (r) => r.technical_tools_software?.items || [],
        setItems: (r, items) => { if (r.technical_tools_software) r.technical_tools_software.items = items; }
    },
    {
        key: 'frameworks_standards',
        label: 'Frameworks & Standards',
        helpKey: 'frameworks',
        getItems: (r) => r.frameworks_standards?.items || [],
        setItems: (r, items) => { if (r.frameworks_standards) r.frameworks_standards.items = items; }
    },
    // Row 4: Communication & Professional/Ethical
    {
        key: 'communication_stakeholder',
        label: 'Communication & Stakeholder',
        helpKey: 'communication',
        getItems: (r) => r.communication_stakeholder?.items || [],
        setItems: (r, items) => { if (r.communication_stakeholder) r.communication_stakeholder.items = items; }
    },
    {
        key: 'professional_ethical',
        label: 'Professional & Ethical Standards',
        helpKey: 'professionalEthical',
        getItems: (r) => r.professional_ethical?.items || [],
        setItems: (r, items) => { if (r.professional_ethical) r.professional_ethical.items = items; }
    },
    // Row 5: Languages & Sector Experience
    {
        key: 'language_requirements',
        label: 'Languages',
        helpKey: 'languages',
        getItems: (r) => r.language_requirements?.items || [],
        setItems: (r, items) => { if (r.language_requirements) r.language_requirements.items = items; }
    },
    {
        key: 'sector_experience',
        label: 'Sector Experience',
        helpKey: 'sectors',
        getItems: (r) => r.sector_experience?.items || [],
        setItems: (r, items) => { if (r.sector_experience) r.sector_experience.items = items; }
    },
    // Row 6: Donor Experience & Education Fields
    {
        key: 'donor_experience',
        label: 'Donor Experience',
        helpKey: 'clientTypes',
        getItems: (r) => r.donor_experience?.items || [],
        setItems: (r, items) => { if (r.donor_experience) r.donor_experience.items = items; }
    },
    {
        key: 'education_fields',
        label: 'Education Fields',
        helpKey: 'educationFields',
        getItems: (r) => r.education_requirements?.fields || [],
        setItems: (r, items) => { if (r.education_requirements) r.education_requirements.fields = items; }
    },
    // Row 7: Experience Types (extracted from ToR - e.g., team leadership, remote management)
    {
        key: 'experience_types',
        label: 'Experience Types',
        helpKey: 'roleLevel',
        getItems: (r) => r.experience_requirements?.experience_types || [],
        setItems: (r, items) => { if (r.experience_requirements) r.experience_requirements.experience_types = items; }
    }
];

const TorCVSearchPageV2: React.FC = () => {
    // CV Source selection
    const [cvSource, setCvSource] = useState<CVSource>('associate');
    
    // Paste and extraction state
    const [pastedText, setPastedText] = useState<string>('');
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionError, setExtractionError] = useState<string | null>(null);
    
    // V2 requirements state - mutable copy for editing
    const [requirements, setRequirements] = useState<ConsultantRequirementsV2 | null>(null);
    const [queryVector, setQueryVector] = useState<number[] | undefined>(undefined);
    
    // Add new item input state per category
    const [newItemInputs, setNewItemInputs] = useState<{[key: string]: string}>({});
    
    // CV matching state
    const [cvMatches, setCvMatches] = useState<CVMatchingResult | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    
    
    // Track if we loaded from Proposal Assistant
    const [prefillSource, setPrefillSource] = useState<string | null>(null);
    
    // Check for pre-filled requirements from Proposal Assistant on mount
    useEffect(() => {
        const prefillData = localStorage.getItem('cv-search-prefill');
        if (prefillData) {
            try {
                const data = JSON.parse(prefillData);
                // Only use if recent (within 5 minutes)
                if (data.timestamp && Date.now() - data.timestamp < 5 * 60 * 1000) {
                    console.log('📋 Loading pre-filled requirements from Proposal Assistant');
                    setRequirements(data.requirements);
                    if (data.queryVector) {
                        setQueryVector(data.queryVector);
                    }
                    setPrefillSource(data.source || 'unknown');
                }
                // Clear the localStorage after reading
                localStorage.removeItem('cv-search-prefill');
            } catch (err) {
                console.error('Failed to parse prefill data:', err);
                localStorage.removeItem('cv-search-prefill');
            }
        }
    }, []);
    
    const handleExtractFromText = async () => {
        if (!pastedText.trim()) return;
        
        setIsExtracting(true);
        setExtractionError(null);
        
        try {
            // Extract consultant requirements from pasted text
            const result = await extractTorFromText(pastedText.trim());
            
            if (result) {
                // The API now returns V2 format directly
                const v2Reqs = result.requirements as unknown as ConsultantRequirementsV2;
                
                // Ensure all categories have proper structure
                const normalizedReqs: ConsultantRequirementsV2 = {
                    project_title: v2Reqs.project_title || 'Consultant Requirements',
                    methods_methodologies: v2Reqs.methods_methodologies || { items: [] },
                    technical_domain_expertise: v2Reqs.technical_domain_expertise || { items: [] },
                    technical_tools_software: v2Reqs.technical_tools_software || { items: [] },
                    frameworks_standards: v2Reqs.frameworks_standards || { items: [] },
                    communication_stakeholder: v2Reqs.communication_stakeholder || { items: [] },
                    professional_ethical: v2Reqs.professional_ethical || { items: [] },
                    geographic_experience: v2Reqs.geographic_experience || { regions: [], countries: [] },
                    language_requirements: v2Reqs.language_requirements || { items: [] },
                    sector_experience: v2Reqs.sector_experience || { items: [] },
                    experience_requirements: v2Reqs.experience_requirements || { 
                        minimum_years: null, 
                        minimum_years_tier: 'neutral',
                        experience_types: [] 
                    },
                    education_requirements: v2Reqs.education_requirements || { 
                        minimum_level: null, 
                        minimum_level_tier: 'neutral',
                        fields: [] 
                    },
                    team_requirements: v2Reqs.team_requirements || { 
                        role: null, 
                        level: null, 
                        team_size: null, 
                        team_composition: null 
                    },
                    donor_experience: v2Reqs.donor_experience || { items: [] },
                    contextual_requirements: v2Reqs.contextual_requirements || {
                        lmic_experience: { value: false, tier: 'neutral' },
                        government_engagement: { value: false, tier: 'neutral' },
                        donor_systems_knowledge: [],
                        national_policy_familiarity: { value: false, tier: 'neutral' },
                        fragile_conflict_states: { value: false, tier: 'neutral' }
                    }
                };
                
                setRequirements(normalizedReqs);
                setQueryVector(result.query_vector);
                
                console.log('📋 V2 Requirements extracted from pasted text:', normalizedReqs);
            } else {
                throw new Error('No requirements extracted from text');
            }
        } catch (err) {
            console.error('Text extraction failed:', err);
            setExtractionError(err instanceof Error ? err.message : 'Failed to extract requirements from text');
        } finally {
            setIsExtracting(false);
        }
    };
    
    // Cycle tier: neutral -> required -> preferred -> bonus -> neutral
    const cycleTier = useCallback((currentTier: RequirementTier): RequirementTier => {
        switch (currentTier) {
            case 'neutral': return 'required';
            case 'required': return 'preferred';
            case 'preferred': return 'bonus';
            case 'bonus': return 'neutral';
            default: return 'neutral';
        }
    }, []);
    
    // Update item tier in a category
    const handleItemTierChange = useCallback((categoryKey: string, itemIndex: number) => {
        if (!requirements) return;
        
        setRequirements(prev => {
            if (!prev) return prev;
            
            const newReqs = { ...prev };
            const category = CATEGORIES.find(c => c.key === categoryKey);
            if (!category) return prev;
            
            const items = [...category.getItems(newReqs)];
            if (items[itemIndex]) {
                items[itemIndex] = {
                    ...items[itemIndex],
                    tier: cycleTier(items[itemIndex].tier)
                };
                category.setItems(newReqs, items);
            }
            
            return newReqs;
        });
    }, [requirements, cycleTier]);
    
    // Remove item from category
    const handleRemoveItem = useCallback((categoryKey: string, itemIndex: number) => {
        if (!requirements) return;
        
        setRequirements(prev => {
            if (!prev) return prev;
            
            const newReqs = { ...prev };
            const category = CATEGORIES.find(c => c.key === categoryKey);
            if (!category) return prev;
            
            const items = category.getItems(newReqs).filter((_, i) => i !== itemIndex);
            category.setItems(newReqs, items);
            
            return newReqs;
        });
    }, [requirements]);
    
    // Add new item to category
    const handleAddItem = useCallback((categoryKey: string) => {
        const text = newItemInputs[categoryKey]?.trim();
        if (!text || !requirements) return;
        
        setRequirements(prev => {
            if (!prev) return prev;
            
            const newReqs = { ...prev };
            const category = CATEGORIES.find(c => c.key === categoryKey);
            if (!category) return prev;
            
            const items = [...category.getItems(newReqs), { text, tier: 'required' as RequirementTier }];
            category.setItems(newReqs, items);
            
            return newReqs;
        });
        
        // Clear input
        setNewItemInputs(prev => ({ ...prev, [categoryKey]: '' }));
    }, [requirements, newItemInputs]);
    
    // Handle input change for new item
    const handleNewItemInputChange = useCallback((categoryKey: string, value: string) => {
        setNewItemInputs(prev => ({ ...prev, [categoryKey]: value }));
    }, []);
    
    // Handle Enter key in input
    const handleInputKeyPress = useCallback((categoryKey: string, event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleAddItem(categoryKey);
        }
    }, [handleAddItem]);
    
    const handleFindMatchingCVs = async () => {
        if (!requirements) return;
        
        setIsSearching(true);
        setSearchError(null);
        
        try {
            console.log('🔍 Sending V2 requirements to backend:', requirements);
            
            // Pass the V2 requirements directly to CV matching
            const result = await matchCVsFromTor(
                { requirements: requirements as any },
                queryVector, 
                20, 
                cvSource
            );
            setCvMatches(result);
        } catch (err) {
            console.error('CV search failed:', err);
            setSearchError(err instanceof Error ? err.message : 'Failed to find matching CVs');
        } finally {
            setIsSearching(false);
        }
    };
    
    const handleReset = () => {
        setPastedText('');
        setRequirements(null);
        setQueryVector(undefined);
        setCvMatches(null);
        setExtractionError(null);
        setSearchError(null);
        setNewItemInputs({});
    };
    
    // Render a single requirement chip with tier color
    const renderTieredChip = (
        categoryKey: string, 
        item: RequirementItem, 
        index: number
    ) => {
        const colors = TIER_COLORS[item.tier];
        return (
            <Chip
                key={`${categoryKey}-${index}`}
                label={item.text}
                size="small"
                onClick={() => handleItemTierChange(categoryKey, index)}
                onContextMenu={(e) => {
                    e.preventDefault();
                    handleRemoveItem(categoryKey, index);
                }}
                sx={{
                    cursor: 'pointer',
                    bgcolor: colors.bg,
                    color: colors.text,
                    borderColor: colors.border,
                    '&:hover': {
                        bgcolor: colors.bg,
                        opacity: 0.85
                    }
                }}
            />
        );
    };
    
    // Render a category section
    const renderCategorySection = (config: CategoryConfig) => {
        if (!requirements) return null;
        
        const items = config.getItems(requirements);
        
        // Always show category if it has items OR if we want to allow adding
        return (
            <Grid item xs={12} md={6} key={config.key}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {config.label}
                    </Typography>
                    {config.helpKey && (
                        <HelpIconButton helpKey={config.helpKey} title={config.label} />
                    )}
                    <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                        {items.length} items
                    </Typography>
                </Box>
                
                {/* Existing items */}
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1, minHeight: 32 }}>
                    {items.map((item, i) => renderTieredChip(config.key, item, i))}
                    {items.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No items extracted. Add below.
                        </Typography>
                    )}
                </Stack>
                
                {/* Add new item input */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder={`Add ${config.label.toLowerCase()}...`}
                        value={newItemInputs[config.key] || ''}
                        onChange={(e) => handleNewItemInputChange(config.key, e.target.value)}
                        onKeyPress={(e) => handleInputKeyPress(config.key, e)}
                        sx={{ flex: 1 }}
                        InputProps={{
                            sx: { fontSize: '0.875rem' }
                        }}
                    />
                    <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleAddItem(config.key)}
                        disabled={!newItemInputs[config.key]?.trim()}
                    >
                        <AddIcon />
                    </IconButton>
                </Box>
            </Grid>
        );
    };
    
    return (
        <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ContentPasteIcon fontSize="large" />
                            ToR CV Search
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Paste consultant requirements from your ToR to find matching CVs using AI-powered semantic search
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* CVSourceToggle hidden - IOD Staff is work in progress, defaulting to Associate */}
                        {/* <CVSourceToggle 
                            value={cvSource} 
                            onChange={setCvSource}
                            disabled={isSearching || isExtracting}
                        /> */}
                        <HelpIconButton 
                            helpKey="main" 
                            title="How CV Matching Works"
                            size="large"
                        />
                    </Box>
                </Box>
            </Box>
            
            {extractionError && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setExtractionError(null)}>
                    {extractionError}
                </Alert>
            )}
            
            {searchError && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSearchError(null)}>
                    {searchError}
                </Alert>
            )}
            
            {/* Pre-filled from Proposal Assistant indicator */}
            {prefillSource === 'proposal-assistant' && requirements && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setPrefillSource(null)}>
                    <strong>Requirements loaded from Proposal Assistant!</strong> Review and adjust the criteria below, then click "Find Matching CVs" to search.
                </Alert>
            )}
            
            {/* Paste Requirements Section */}
            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Paste ToR Requirements
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Copy and paste the skills/requirements section from your ToR document below. 
                    The system will extract key attributes for CV matching.
                </Typography>
                
                <TextField
                    multiline
                    rows={8}
                    fullWidth
                    placeholder="Paste the consultant requirements section from your ToR here...

Example:
• Excellent demonstrated experience leading evaluations of cash-based interventions
• Experience in applying rigorous analytical methods including impact evaluation
• Proven experience in management consulting and organisational transformation
• A post-graduate degree in social sciences, economics, or related field
• Fluent English writing skills; French is an advantage"
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    disabled={isExtracting}
                    sx={{ mb: 3 }}
                />
                
                {isExtracting && (
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Extracting requirements...
                        </Typography>
                    </Box>
                )}
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                        startIcon={<RefreshIcon />}
                        disabled={!pastedText && !requirements}
                    >
                        Reset
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleExtractFromText}
                        disabled={!pastedText.trim() || isExtracting}
                        startIcon={isExtracting ? <CircularProgress size={20} /> : <SearchIcon />}
                    >
                        {isExtracting ? 'Extracting...' : 'Extract Requirements'}
                    </Button>
                </Box>
            </Paper>
            
            {/* Extracted Requirements Display */}
            {requirements && (
                <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                        {requirements.project_title || 'Extracted Requirements'}
                    </Typography>
                    
                    {/* Legend */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            Click chip to cycle tier:
                        </Typography>
                        <Chip 
                            label="Required" 
                            size="small" 
                            sx={{ bgcolor: TIER_COLORS.required.bg, color: 'white' }} 
                        />
                        <Typography variant="body2" color="text.secondary">→</Typography>
                        <Chip 
                            label="Preferred" 
                            size="small" 
                            sx={{ bgcolor: TIER_COLORS.preferred.bg, color: 'white' }} 
                        />
                        <Typography variant="body2" color="text.secondary">→</Typography>
                        <Chip 
                            label="Bonus" 
                            size="small" 
                            sx={{ bgcolor: TIER_COLORS.bonus.bg, color: 'white' }} 
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            | Right-click to remove
                        </Typography>
                    </Box>
                    
                    {/* Experience & Education - Always visible with dropdowns */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {/* Minimum Years of Experience */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Minimum Years of Experience
                                </Typography>
                                <HelpIconButton helpKey="yearsExperience" title="Years of Experience" />
                            </Box>
                            {requirements.experience_requirements?.minimum_years ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Chip 
                                        label={`${requirements.experience_requirements.minimum_years}+ years`}
                                        size="small"
                                        sx={{ 
                                            bgcolor: TIER_COLORS[requirements.experience_requirements.minimum_years_tier || 'required'].bg,
                                            color: 'white',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => {
                                            setRequirements(prev => {
                                                if (!prev) return prev;
                                                return {
                                                    ...prev,
                                                    experience_requirements: {
                                                        ...prev.experience_requirements,
                                                        minimum_years_tier: cycleTier(prev.experience_requirements.minimum_years_tier)
                                                    }
                                                };
                                            });
                                        }}
                                        onDelete={() => {
                                            setRequirements(prev => {
                                                if (!prev) return prev;
                                                return {
                                                    ...prev,
                                                    experience_requirements: {
                                                        ...prev.experience_requirements,
                                                        minimum_years: null
                                                    }
                                                };
                                            });
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        (click to change tier, × to remove)
                                    </Typography>
                                </Stack>
                            ) : (
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    value=""
                                    onChange={(e) => {
                                        const years = parseInt(e.target.value);
                                        if (!isNaN(years)) {
                                            setRequirements(prev => {
                                                if (!prev) return prev;
                                                return {
                                                    ...prev,
                                                    experience_requirements: {
                                                        ...prev.experience_requirements,
                                                        minimum_years: years,
                                                        minimum_years_tier: 'preferred'
                                                    }
                                                };
                                            });
                                        }
                                    }}
                                >
                                    <MenuItem value="">Select experience level (optional)</MenuItem>
                                    <MenuItem value="5">5+ years</MenuItem>
                                    <MenuItem value="10">10+ years</MenuItem>
                                    <MenuItem value="15">15+ years</MenuItem>
                                    <MenuItem value="20">20+ years</MenuItem>
                                </TextField>
                            )}
                        </Grid>
                        
                        {/* Minimum Education Level */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Minimum Education Level
                                </Typography>
                                <HelpIconButton helpKey="educationLevel" title="Education Level" />
                            </Box>
                            {requirements.education_requirements?.minimum_level ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Chip 
                                        label={requirements.education_requirements.minimum_level}
                                        size="small"
                                        sx={{ 
                                            bgcolor: TIER_COLORS[requirements.education_requirements.minimum_level_tier || 'required'].bg,
                                            color: 'white',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => {
                                            setRequirements(prev => {
                                                if (!prev) return prev;
                                                return {
                                                    ...prev,
                                                    education_requirements: {
                                                        ...prev.education_requirements,
                                                        minimum_level_tier: cycleTier(prev.education_requirements.minimum_level_tier)
                                                    }
                                                };
                                            });
                                        }}
                                        onDelete={() => {
                                            setRequirements(prev => {
                                                if (!prev) return prev;
                                                return {
                                                    ...prev,
                                                    education_requirements: {
                                                        ...prev.education_requirements,
                                                        minimum_level: null
                                                    }
                                                };
                                            });
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        (click to change tier, × to remove)
                                    </Typography>
                                </Stack>
                            ) : (
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    value=""
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            setRequirements(prev => {
                                                if (!prev) return prev;
                                                return {
                                                    ...prev,
                                                    education_requirements: {
                                                        ...prev.education_requirements,
                                                        minimum_level: e.target.value,
                                                        minimum_level_tier: 'preferred'
                                                    }
                                                };
                                            });
                                        }
                                    }}
                                >
                                    <MenuItem value="">Select education level (optional)</MenuItem>
                                    <MenuItem value="Bachelor's">Bachelor's Degree</MenuItem>
                                    <MenuItem value="Master's">Master's Degree</MenuItem>
                                    <MenuItem value="PhD">PhD/Doctorate</MenuItem>
                                </TextField>
                            )}
                        </Grid>
                    </Grid>
                    
                    {/* Category sections */}
                    <Grid container spacing={3}>
                        {CATEGORIES.map(config => renderCategorySection(config))}
                    </Grid>
                    
                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
                        <Button
                            variant="outlined"
                            onClick={handleReset}
                            startIcon={<RefreshIcon />}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleFindMatchingCVs}
                            disabled={isSearching}
                            startIcon={isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                        >
                            {isSearching ? 'Searching CVs...' : 'Find Matching CVs'}
                        </Button>
                    </Box>
                </Paper>
            )}
            
            {/* Results */}
            {cvMatches && cvMatches.matches.length > 0 && (
                <CVMatchingResults 
                    matches={cvMatches.matches}
                    additionalMatches={cvMatches.additional_matches}
                    searchCriteria={cvMatches.search_criteria}
                    selectedCategories={cvMatches.selected_categories}
                />
            )}
            
            {cvMatches && cvMatches.matches.length === 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <AlertTitle>No CVs Found Matching All Required Criteria</AlertTitle>
                    
                    {cvMatches.failure_analysis ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Checked {cvMatches.failure_analysis.total_cvs_checked.toLocaleString()} CVs — none have all required criteria.
                            </Typography>
                            
                            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                                Consider changing these to Preferred (in order of impact):
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {[...cvMatches.failure_analysis.criteria_failure_counts]
                                    .sort((a, b) => b.failed_count - a.failed_count)
                                    .slice(0, 5)
                                    .map((item, idx) => (
                                        <Chip 
                                            key={idx}
                                            label={`${idx + 1}. ${item.criterion}`} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: TIER_COLORS.required.bg, 
                                                color: 'white'
                                            }}
                                        />
                                    ))}
                            </Box>
                            
                            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                                💡 Click a red chip in your search criteria to change it to Preferred.
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2">
                            Try broadening your search by changing some Required criteria to Preferred.
                        </Typography>
                    )}
                </Alert>
            )}
        </Box>
    );
};

export default TorCVSearchPageV2;
